'use client';

import { useEffect, useRef, useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';

import type { TestimonialBox, TestimonialsData } from './types';

interface Props {
  data: TestimonialsData;
}

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 1.5L11.163 6.28L16.5 6.927L12.75 10.557L13.727 15.87L9 13.245L4.273 15.87L5.25 10.557L1.5 6.927L6.837 6.28L9 1.5Z"
      fill="#F59E0B"
      stroke="#F59E0B"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

const TestimonialCard = ({ box }: { box: TestimonialBox }) => (
  <div className="from-theme-primary-light/70 via-lavender-300/55 to-info-300/65 flex h-full min-w-0 flex-col gap-4 rounded-2xl bg-linear-to-br p-6">
    <span className="text-theme-primary text-lg font-bold">{box.name}</span>
    <p className="text-text-default flex-1 text-base leading-relaxed">
      {box.text}
    </p>
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <StarIcon key={i} />
      ))}
    </div>
    <span className="text-text-placeholder text-xs">{box.date}</span>
  </div>
);

const NavButton = ({
  onClick,
  direction,
  disabled,
}: {
  onClick: () => void;
  direction: 'prev' | 'next';
  disabled: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={direction === 'prev' ? 'Previous' : 'Next'}
    className="border-gray-150 flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-full border bg-white text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-all duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
  >
    <svg
      className="text-text-main h-5 w-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d={
          direction === 'prev'
            ? 'M15.75 19.5L8.25 12l7.5-7.5'
            : 'M8.25 4.5l7.5 7.5-7.5 7.5'
        }
      />
    </svg>
  </button>
);

const AUTOPLAY_INTERVAL = 2000;

function useCardsPerView() {
  const [cardsPerView, setCardsPerView] = useState(3);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setCardsPerView(1);
      else if (window.innerWidth < 1024) setCardsPerView(2);
      else setCardsPerView(3);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return cardsPerView;
}

const Testimonials = ({ data }: Props) => {
  const { heading, description, sliderContent } = data;
  const [activeIndex, setActiveIndex] = useState(0);
  const isPaused = useRef(false);
  const cardsPerView = useCardsPerView();

  const maxIndex = Math.max(0, sliderContent.length - cardsPerView);

  useEffect(() => {
    setActiveIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isPaused.current) {
        setActiveIndex((i) => (i >= maxIndex ? 0 : i + 1));
      }
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [maxIndex]);

  if (!sliderContent.length) return null;

  const prev = () => setActiveIndex((i) => Math.max(0, i - 1));
  const next = () => setActiveIndex((i) => Math.min(maxIndex, i + 1));
  const gap = 24;

  return (
    <section
      className="w-full"
      onMouseEnter={() => {
        isPaused.current = true;
      }}
      onMouseLeave={() => {
        isPaused.current = false;
      }}
    >
      <div className="md:content-wrapper px-2">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <RichText styles={heading.styles}>{heading.content}</RichText>
          <RichText styles={description.styles}>{description.content}</RichText>
        </div>

        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              gap: `${gap}px`,
              transform: `translateX(calc(-${activeIndex} * (100% / ${cardsPerView} + ${gap / cardsPerView}px)))`,
            }}
          >
            {sliderContent.map((box, index) => (
              <div
                key={index}
                className="shrink-0"
                style={{
                  width: `calc(${100 / cardsPerView}% - ${((cardsPerView - 1) * gap) / cardsPerView}px)`,
                }}
              >
                <TestimonialCard box={box} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <NavButton
            onClick={prev}
            direction="prev"
            disabled={activeIndex === 0}
          />
          {sliderContent.length > cardsPerView && (
            <div className="flex items-center gap-1.5">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? 'bg-theme-brand-blue w-4'
                      : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
          <NavButton
            onClick={next}
            direction="next"
            disabled={activeIndex >= maxIndex}
          />
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
